<template>
    <div class="columns">
        <div class="column" v-if="registeredCompetitors.length > 0">
            <table class="table is-fullwidth is-hoverable is-hidden-touch">
                <thead>
                    <tr>
                        <th>#</th>
                        <th>
                            <a @click="changeSortField('lastName')">
                                <span>{{ translate("common.first_name_and_last_name") }}</span>
                                <span class="icon">
                                    <i class="fa fa-sort" />
                                </span>
                            </a>
                        </th>
                        <th>
                            {{ translate("common.category") }}
                        </th>
                        <th>
                            <a @click="changeSortField('created')">
                                <span>
                                    {{ translate("common.registration_date") }}
                                </span>
                                <span class="icon">
                                    <i class="fa fa-sort" />
                                </span>
                            </a>
                        </th>
                        <th>{{ translate("common.status") }}</th>
                        <th>{{ translate("admin_payments.payment_status") }}</th>
                        <th>{{ translate("admin_payments.documents_sent") }}</th>
                        <th></th>
                    </tr>
                </thead>

                <tbody>
                    <tr
                        v-for="(registeredCompetitor, index) in registeredCompetitors"
                        :key="registeredCompetitor.id"
                        @click="conditionallySelectCompetitorInBulkMode(registeredCompetitor.id)"
                        :class="{ 'has-pointer-cursor': bulkModeEnabled }"
                    >
                        <td>
                            <template v-if="bulkModeEnabled">
                                <Checkbox2
                                    :model-value="store.selectedCompetitors[registeredCompetitor.id]"
                                    @click="conditionallySelectCompetitorInBulkMode(registeredCompetitor.id)"
                                />
                            </template>
                            <template v-else>
                                {{ index + 1 + offset }}
                            </template>
                        </td>
                        <td>
                            <Country :country-code="registeredCompetitor.nationality" :only-flag="true" />
                            <span class="clarity-mask"> {{ registeredCompetitor.firstName }} {{ registeredCompetitor.lastName }} </span>
                            <p class="competitor-academy">{{ registeredCompetitor.academy }} / {{ registeredCompetitor.branch }}</p>
                        </td>
                        <td>
                            <a @click.stop="openCategorySidebar(registeredCompetitor.parameterId)">{{ registeredCompetitor.category }}</a>
                        </td>
                        <td>
                            <RegistrationStageNumber
                                :stage-number="registeredCompetitor.stageNumber"
                                :date="registeredCompetitor.creationDate"
                                :timezone="eventTimezone"
                            />
                        </td>
                        <td>
                            <RegisteredCompetitorStatusTag :status="registeredCompetitor.status.value" />
                        </td>
                        <td>
                            <CompetitorPaymentStatus :competitor="registeredCompetitor" />
                        </td>
                        <td>
                            <RegisteredCompetitorDocumentsTag :competitor="registeredCompetitor" />
                        </td>
                        <td>
                            <RegisteredCompetitorOptions
                                :registered-competitor="registeredCompetitor"
                                :event-id="eventId"
                                :is-full-organizer="store.isFullOrganizer"
                            />
                        </td>
                    </tr>
                </tbody>
            </table>

            <div class="is-hidden-desktop">
                <div class="field">
                    <div class="control has-icons-left">
                        <div class="select is-fullwidth">
                            <select
                                :value="`${sortField}:${sortDirection}`"
                                @change="changeSort(($event.target as HTMLSelectElement).value)"
                            >
                                <option v-for="option in sortOptions" :key="option.value" :value="option.value">
                                    {{ translate("common.sort") }}: {{ option.label }}
                                </option>
                            </select>
                        </div>
                        <span class="icon is-left">
                            <i class="fa fa-sort" />
                        </span>
                    </div>
                </div>

                <div
                    v-for="(registeredCompetitor, index) in registeredCompetitors"
                    :key="registeredCompetitor.id"
                    class="box has-no-glow-effect"
                    :class="{
                        'has-pointer-cursor': bulkModeEnabled,
                        'is-selected': bulkModeEnabled && store.selectedCompetitors[registeredCompetitor.id],
                    }"
                    @click="conditionallySelectCompetitorInBulkMode(registeredCompetitor.id)"
                >
                    <div class="is-flex is-align-items-flex-start">
                        <div class="competitor-card-index mr-3">
                            <div v-if="bulkModeEnabled" @click.stop>
                                <Checkbox2
                                    :model-value="store.selectedCompetitors[registeredCompetitor.id]"
                                    @update:model-value="store.toggleSelectedCompetitor(registeredCompetitor.id, $event)"
                                />
                            </div>
                            <span v-else class="has-text-white has-text-weight-bold">#{{ index + 1 + offset }}</span>
                        </div>
                        <div>
                            <p class="has-text-weight-bold has-text-white is-size-6">
                                <Country :country-code="registeredCompetitor.nationality" :only-flag="true" />
                                <span class="clarity-mask">{{ registeredCompetitor.firstName }} {{ registeredCompetitor.lastName }}</span>
                            </p>
                            <p class="competitor-academy">{{ registeredCompetitor.academy }} / {{ registeredCompetitor.branch }}</p>
                            <div class="mt-2">
                                <RegisteredCompetitorStatusTag :status="registeredCompetitor.status.value" />
                            </div>
                        </div>
                    </div>

                    <div class="competitor-card-details is-size-7 mt-4">
                        <span class="has-text-grey-light">{{ translate("common.category") }}</span>
                        <span
                            ><a @click.stop="openCategorySidebar(registeredCompetitor.parameterId)">{{
                                registeredCompetitor.category
                            }}</a></span
                        >
                        <span class="has-text-grey-light">{{ translate("common.registration_date") }}</span>
                        <span class="has-text-white">
                            <RegistrationStageNumber
                                :stage-number="registeredCompetitor.stageNumber"
                                :date="registeredCompetitor.creationDate"
                                :timezone="eventTimezone"
                            />
                        </span>
                        <span class="has-text-grey-light">{{ translate("admin_payments.payment_status") }}</span>
                        <span>
                            <CompetitorPaymentStatus :competitor="registeredCompetitor" />
                        </span>
                        <span class="has-text-grey-light">{{ translate("admin_payments.documents_sent") }}</span>
                        <span>
                            <RegisteredCompetitorDocumentsTag :competitor="registeredCompetitor" />
                        </span>
                    </div>

                    <RegisteredCompetitorOptions
                        v-if="!bulkModeEnabled"
                        class="competitor-card-actions mt-4"
                        :registered-competitor="registeredCompetitor"
                        :event-id="eventId"
                        :is-full-organizer="store.isFullOrganizer"
                    />
                </div>
            </div>
        </div>
        <div v-else-if="initDone && registeredCompetitors.length === 0" class="column is-4 is-offset-4">
            <Notification class="is-info">
                <span class="icon">
                    <i class="fa fa-info-circle" />
                </span>
                <span>{{ translate("common.no_results") }}</span>
            </Notification>
        </div>
    </div>
</template>

<script setup lang="ts">
    import { computed } from "vue";
    import Country from "../Common/Country.vue";
    import Notification from "../Common/Notification.vue";
    import CompetitorPaymentStatus from "./Payments/CompetitorPaymentStatus.vue";
    import RegistrationStageNumber from "./Payments/RegistrationStageNumber.vue";
    import RegisteredCompetitorOptions from "./RegisteredCompetitorOptions.vue";
    import RegisteredCompetitorStatusTag from "./RegisteredCompetitorStatusTag.vue";
    import RegisteredCompetitorDocumentsTag from "./RegisteredCompetitorDocumentsTag.vue";
    import { DEFAULT_SORT_DIRECTION } from "./adminRegisteredCompetitorsApp";
    import history from "../Common/history";
    import { translate } from "../Common/translations";
    import Checkbox2 from "../Common/Checkbox2.vue";
    import { CompetitorPaymentWithMetadata, SortDirection, SortField } from "../Api/payments";
    import { useRegisteredCompetitorsStore } from "./registeredCompetitorsStore";
    import { eventBus, OPEN_CATEGORY_SIDEBAR } from "./eventBus";

    const store = useRegisteredCompetitorsStore();
    const historyInstance = history.getInstance(window);

    const registeredCompetitors = computed<CompetitorPaymentWithMetadata[]>(() => store.registeredCompetitors);
    const eventTimezone = computed(() => store.eventTimezone);
    const offset = computed(() => store.offset);
    const eventId = computed(() => store.eventId);
    const initDone = computed(() => store.initDone);
    const sortField = computed(() => store.sortField);
    const sortDirection = computed(() => store.sortDirection);
    const bulkModeEnabled = computed(() => store.bulkModeEnabled);

    const openCategorySidebar = (parameterId: number) => {
        eventBus.$emit(OPEN_CATEGORY_SIDEBAR, parameterId);
    };

    const conditionallySelectCompetitorInBulkMode = (eventRegistrationId: number) => {
        if (bulkModeEnabled.value) {
            store.toggleSelectedCompetitor(eventRegistrationId, !store.selectedCompetitors[eventRegistrationId]);
        }
    };

    const applySort = (field: SortField, direction: SortDirection) => {
        store.sortField = field;
        store.sortDirection = direction;

        historyInstance.setQueryParam("sortField", store.sortField);
        historyInstance.setQueryParam("sortDirection", store.sortDirection);

        return store.getRegisteredCompetitorsPage().catch(console.error);
    };

    const changeSortField = (fieldName: SortField) => {
        const direction = sortField.value === fieldName ? (sortDirection.value === "asc" ? "desc" : "asc") : DEFAULT_SORT_DIRECTION;

        return applySort(fieldName, direction);
    };

    const sortOptions = computed(() => {
        const fields: { field: SortField; label: string }[] = [
            { field: "lastName", label: translate("common.first_name_and_last_name") },
            { field: "created", label: translate("common.registration_date") },
        ];

        return fields.flatMap(({ field, label }) => [
            { value: `${field}:asc`, label: `${label} ↑` },
            { value: `${field}:desc`, label: `${label} ↓` },
        ]);
    });

    const changeSort = (value: string) => {
        const [field, direction] = value.split(":");

        return applySort(field as SortField, direction as SortDirection);
    };
</script>
